/**
 * Settings > Time capture: what CaseBlink may use to draft this person's
 * time, when the evening review runs, and what it has learned. Every change
 * saves on its own; nothing here is visible to anyone else.
 */
import { Plug, ShieldCheck, X } from "lucide-react";
import { useState, type FC, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { CONNECTORS_PATH } from "@/features/connectors/routes";
import { queryClient } from "@/lib/query/queryClient";

import { TimeAPI } from "../api/timeApi";
import { reviewTimeLabel } from "../home/homeModel";
import { refreshTime, timeKeys, useMyTimeSettings } from "../queries";
import { setTrackingPaused } from "../tracker/pause";
import { useTrackerStore } from "../tracker/trackerStore";
import type { MyTimeSettings, MyTimeSettingsPatch } from "../types";
import { Segmented, SettingRow } from "./SettingRow";

const REVIEW_TIMES = [
  { id: "17:00", label: "5 PM" },
  { id: "18:00", label: "6 PM" },
  { id: "19:00", label: "7 PM" }
] as const;

type ReviewTime = (typeof REVIEW_TIMES)[number]["id"];

const asReviewTime = (v: string): ReviewTime =>
  REVIEW_TIMES.find((t) => t.id === v)?.id ?? "18:00";

export const parseNeverCapture = (text: string): string[] =>
  Array.from(
    new Set(
      text
        .split(/[\s,;]+/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
    )
  );

const browserTimezone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

const saveMine = async (patch: MyTimeSettingsPatch): Promise<void> => {
  queryClient.setQueryData<MyTimeSettings>(timeKeys.mySettings(), (old) =>
    old ? { ...old, ...patch } : old
  );
  try {
    const saved = await TimeAPI.saveMySettings(patch);
    queryClient.setQueryData(timeKeys.mySettings(), saved);
  } catch (error) {
    toast.error(
      error instanceof Error ? error.message : "Couldn't save your settings"
    );
  } finally {
    void refreshTime();
  }
};

type SourceProps = {
  badge: string;
  name: string;
  sub: string;
  primary?: boolean;
  children: ReactNode;
};

const Source: FC<SourceProps> = ({ badge, name, sub, primary, children }) => (
  <div className="flex items-center gap-3 border-b border-line-2 px-3.5 py-3 last:border-b-0">
    <span
      className={
        primary
          ? "inline-flex size-7.5 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-medium text-primary-foreground"
          : "inline-flex size-7.5 shrink-0 items-center justify-center rounded-lg border border-line-2 bg-fill-2 text-sm font-medium text-fg-3"
      }
    >
      {badge}
    </span>
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="text-base font-medium">{name}</span>
      <span className="truncate text-sm text-fg-3">{sub}</span>
    </span>
    {children}
  </div>
);

type Props = { onRequestClose?: () => void };

export const TimeCaptureSettings: FC<Props> = ({ onRequestClose }) => {
  const navigate = useNavigate();
  const { data: settings, isLoading } = useMyTimeSettings();
  const paused = useTrackerStore((s) => s.paused);
  const [neverText, setNeverText] = useState<string | null>(null);

  if (isLoading || !settings) {
    return (
      <div className="flex flex-col gap-3" data-testid="time-capture-loading">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  const firm = settings.firm?.sources ?? {};
  const connections = settings.connections ?? {};
  const connect = () => {
    onRequestClose?.();
    navigate(CONNECTORS_PATH);
  };
  const offByFirm = "Turned off for your firm";
  const tz = browserTimezone();
  const neverValue = neverText ?? settings.never_capture.join(", ");

  const sourceSwitch = (
    label: string,
    checked: boolean,
    onChange: (next: boolean) => void,
    firmAllows: boolean
  ) => (
    <Switch
      aria-label={label}
      checked={firmAllows && checked}
      disabled={!firmAllows}
      onCheckedChange={onChange}
    />
  );

  return (
    <div className="flex flex-col" data-testid="time-capture-settings">
      <p className="text-sm text-fg-3">
        What CaseBlink uses to draft your time. These settings are just for you.
      </p>

      <div className="mt-4 shrink-0 overflow-hidden rounded-xl border border-line-2">
        <Source
          badge="CB"
          primary
          name="Work in CaseBlink"
          sub={
            firm.web === false
              ? offByFirm
              : "Time on an open case while you're active"
          }
        >
          {sourceSwitch(
            "Capture work in CaseBlink",
            !paused,
            (on) => setTrackingPaused(!on),
            firm.web !== false
          )}
        </Source>
        <Source
          badge="@"
          name="Email"
          sub={
            firm.email === false
              ? offByFirm
              : "Gmail or Outlook · sent mail to a client or petitioner contact"
          }
        >
          {connections.email || firm.email === false ? (
            sourceSwitch(
              "Capture email",
              settings.email_capture,
              (on) => void saveMine({ email_capture: on }),
              firm.email !== false
            )
          ) : (
            <Button size="sm" variant="secondary" onClick={connect}>
              <Plug />
              Connect
            </Button>
          )}
        </Source>
        <Source
          badge="31"
          name="Calendar"
          sub={
            firm.calendar === false
              ? offByFirm
              : "Past events with a client or petitioner contact"
          }
        >
          {connections.calendar || firm.calendar === false ? (
            sourceSwitch(
              "Capture calendar",
              settings.calendar_capture,
              (on) => void saveMine({ calendar_capture: on }),
              firm.calendar !== false
            )
          ) : (
            <Button size="sm" variant="secondary" onClick={connect}>
              <Plug />
              Connect
            </Button>
          )}
        </Source>
      </div>

      <SettingRow
        title="Only during work hours"
        desc={`Ignore activity outside ${reviewTimeLabel(
          settings.work_hours_start
        )} – ${reviewTimeLabel(settings.work_hours_end)} on weekdays.`}
      >
        {settings.work_hours_only ? (
          <>
            <Input
              type="time"
              aria-label="Work starts"
              className="h-8 w-26"
              defaultValue={settings.work_hours_start}
              onBlur={(e) => {
                const v = e.currentTarget.value;
                if (v && v !== settings.work_hours_start)
                  void saveMine({ work_hours_start: v });
              }}
            />
            <Input
              type="time"
              aria-label="Work ends"
              className="h-8 w-26"
              defaultValue={settings.work_hours_end}
              onBlur={(e) => {
                const v = e.currentTarget.value;
                if (v && v !== settings.work_hours_end)
                  void saveMine({ work_hours_end: v });
              }}
            />
          </>
        ) : null}
        <Switch
          aria-label="Only during work hours"
          checked={settings.work_hours_only}
          onCheckedChange={(on) => void saveMine({ work_hours_only: on })}
        />
      </SettingRow>

      <SettingRow
        title="Never capture"
        desc="Email addresses or domains that are never matched to a case."
      >
        <Input
          aria-label="Never capture"
          className="h-8 w-65"
          placeholder="gmail.com, family@example.com"
          value={neverValue}
          onChange={(e) => setNeverText(e.currentTarget.value)}
          onBlur={() => {
            if (neverText === null) return;
            const next = parseNeverCapture(neverText);
            setNeverText(null);
            if (next.join(",") !== settings.never_capture.join(","))
              void saveMine({ never_capture: next });
          }}
        />
      </SettingRow>

      <SettingRow
        title="Evening review"
        desc="Casey posts a summary of your day on Home, with your drafted entries ready to release."
      >
        {settings.evening_review ? (
          <Segmented
            label="Evening review time"
            value={asReviewTime(settings.evening_review_time)}
            options={REVIEW_TIMES}
            onChange={(v) => void saveMine({ evening_review_time: v })}
          />
        ) : null}
        <Switch
          aria-label="Evening review"
          checked={settings.evening_review}
          onCheckedChange={(on) => void saveMine({ evening_review: on })}
        />
      </SettingRow>

      <SettingRow
        title="Remind me the next morning"
        desc="If a day isn't released by midnight, it carries over to Home and the timesheet."
      >
        <Switch
          aria-label="Remind me the next morning"
          checked={settings.morning_reminder}
          onCheckedChange={(on) => void saveMine({ morning_reminder: on })}
        />
      </SettingRow>

      <SettingRow
        title="Time zone"
        desc={`Days and the evening review follow ${settings.timezone}.`}
      >
        {tz && tz !== settings.timezone ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void saveMine({ timezone: tz })}
          >
            Use {tz}
          </Button>
        ) : null}
      </SettingRow>

      <div className="mt-5 flex flex-col">
        <span className="text-sm font-medium text-fg-3">
          What CaseBlink has learned
        </span>
        {settings.match_preferences.length ? (
          <ul className="mt-2 flex flex-col rounded-xl border border-line-2">
            {settings.match_preferences.map((p) => (
              <li
                key={p.id}
                className="flex items-center gap-3 border-b border-line-2 px-3.5 py-2.5 text-base last:border-b-0"
              >
                <span className="min-w-0 flex-1 truncate">
                  From now on, {p.label}
                </span>
                {p.hits ? (
                  <span className="text-sm text-fg-3">Used {p.hits}×</span>
                ) : null}
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label={`Forget: ${p.label}`}
                  onClick={() => {
                    TimeAPI.deletePreference(p.id).then(refreshTime, () =>
                      toast.error("Couldn't remove that")
                    );
                  }}
                >
                  <X />
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1.5 text-sm text-fg-3">
            Nothing yet. When you move an email or meeting to a different case,
            CaseBlink remembers the sender or domain for next time.
          </p>
        )}
      </div>

      <div className="mt-6 flex flex-col gap-2 rounded-xl border border-line-2 bg-fill-1 p-3.5">
        <span className="flex items-center gap-2 text-base font-medium">
          <ShieldCheck className="size-3.5 text-primary" /> What&apos;s stored
        </span>
        <span className="grid grid-cols-2 gap-1.5 text-sm leading-4.5 text-fg-3">
          <span>
            For email: recipients, subject, word count and the matched case. The
            body is read once to match a case and never stored.
          </span>
          <span>
            For meetings: title, time and attendees. Descriptions are read once
            to match a case, then dropped.
          </span>
          <span>
            In the app: which case and document was open, and for how long. No
            keystrokes or screen content.
          </span>
          <span>
            Everything captured is private to you until you release an entry.
          </span>
        </span>
      </div>
    </div>
  );
};
